<script lang="ts" setup>
import ChatMessageItem from "@/components/mobile/ChatMessageItem.vue";
import { useMateDispatchMessageList } from "@/composable/chat/useMateDispatchMessageList";
import { useMate } from "@/stores/mate";
import { useMission } from "@/stores/mission";
import {
  ChatMessageItemInstance,
  MessageListInstance,
} from "@/types/component";
import { Mate } from "@/types/entity";
import { List } from "vant";
import { computed, nextTick, ref } from "vue";
import { isLockMsg } from "../mode/util";
import { BubbleActionPopupInstance } from "../popup/BubbleActionPopup.vue";

const props = withDefaults(
  defineProps<{
    uuid: string;
    mate?: Mate;
    myMessage: string;
    streamMsg: string;
    isPostingMsg: "start" | "end" | false;
    isStreamReplying: boolean;
    enableTool?: boolean;
    enableReactive?: boolean;
  }>(),
  {
    enableTool: false,
    enableReactive: true,
  }
);

defineEmits<{
  (e: "on-continue-btn-click", targets: string[]): void;
}>();

const mateStore = useMate();
const missionStore = useMission();

const currentMateDispatch = computed(
  () => missionStore.uuid2MateDispatchMap[props.uuid]
);
// Element Ref
const messageContainerRef = ref<HTMLElement | null>(null);
const scrollRef = ref<HTMLDivElement | null>(null);
const bubbleActionRef = ref<BubbleActionPopupInstance | null>(null);
const chatMessageRefs = ref<ChatMessageItemInstance[]>([]);

const {
  messageList,
  scrollLoading,
  finished,
  error,
  scrollLoad,
  scrollToBottom,
} = useMateDispatchMessageList({
  uuid: props.uuid,
  messageContainerRef,
  scrollRef,
  myMessage: computed(() => props.myMessage),
  streamMsg: computed(() => props.streamMsg),
  isStreamReplying: computed(() => props.isStreamReplying),
});

const isPrivate = ref(true);

const playAudioMsg = (id: number) => {
  nextTick(() => {
    const target = chatMessageRefs.value.find((item) => item.id === id);
    if (target) {
      target.audioRef?.playAudio();
    }
  });
};

defineExpose<MessageListInstance>({
  scrollToBottom: scrollToBottom,
  playAudioMsg: playAudioMsg,
});
</script>

<template>
  <div ref="scrollRef" class="h-full overflow-y-scroll">
    <div v-if="!mate || !currentMateDispatch"></div>
    <list
      v-else
      v-model:error="error"
      v-model:loading="scrollLoading"
      :error-text="$t('Message.requestFailedClickToReload')"
      :finished="finished"
      :finished-text="$t('Message.noMoreAvailable')"
      :immediate-check="false"
      direction="up"
      offset="0"
      @load="scrollLoad"
    >
      <div
        v-if="false && isPrivate"
        class="flex items-center justify-center p-4 border-b 0"
      >
        <div class="text-base-content/50">
          <span class="font-semibold"
            >{{ $t("User.mate.module.chatScene") }}: </span
          ><span class="chat-container" v-html="''"></span>
        </div>
      </div>
      <div ref="messageContainerRef" class="p-4">
        <chat-message-item
          v-for="item in messageList"
          :id="item.id"
          :key="item.id"
          ref="chatMessageRefs"
          :avatar-url="item.mediaUrl"
          :enable-avatar-url="!!item.mediaUrl"
          :enable-reactive="enableReactive && isPrivate"
          :is-locked="isLockMsg(item)"
          :mate-uuid="item.mateUuid"
          :media="item.media"
          :msg="item.msg"
          :position="item.isCurrentUser ? 'end' : 'start'"
          :require-unlock="item.requireUnlock"
          :time="!isPrivate ? item.time : undefined"
          enable-avatar-click
        />
        <div>
          <chat-message-item
            v-show="myMessage"
            :avatar-url="mateStore.currentMate!.mediaUrl"
            :enable-avatar-url="false"
            :mate-uuid="mateStore.currentMate!.uuid"
            :msg="myMessage"
            :position="'end'"
            enable-avatar-click
            :enable-audio="false"
          />
          <!-- StreamMsg返回中 -->
          <chat-message-item
            v-show="streamMsg"
            :avatar-url="isPostingMsg === 'start' ? mate!.mediaUrl : mateStore.currentMate?.mediaUrl"
            :enable-avatar-url="false"
            :mate-uuid="mate!.uuid"
            :msg="streamMsg"
            :position="isPostingMsg === 'start' ? 'start' : 'end'"
            enable-avatar-click
            enable-parse-md
            :enable-audio="false"
          />
          <!-- StreamMsg返回前的loading -->
          <chat-message-item
            v-show="!streamMsg && isPostingMsg"
            :avatar-url="isPostingMsg === 'start' ? mate!.mediaUrl : mateStore.currentMate?.mediaUrl"
            :enable-avatar-url="false"
            :mate-uuid="mate!.uuid"
            :position="isPostingMsg === 'start' ? 'start' : 'end'"
            enable-avatar-click
            :enable-audio="false"
          >
            <div class="flex items-center">
              <span class="loading loading-dots loading-sm text-base-content" />
            </div>
          </chat-message-item>
        </div>
      </div>
    </list>
    <BubbleActionPopup ref="bubbleActionRef" />
  </div>
</template>
